import { SearchParamController } from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import ConfigurationDelegate from './configuration.delegate';
import SegmentationController from './controllers/Segmentation.controller';
import AvailableBalanceController from './controllers/AvailableBalance.controller';
import FeeStructureController from './controllers/FeeStructure.controller';
import LimitsController from './controllers/Limits.controller';
import {
    ACCESS_RULES_PERSIST_ENABLED,
    pageMetadata,
    SUCCESS_TAB_KEY,
    TAB_SEARCH_PARAM_KEY,
    tabs
} from './configuration.constants';
import { SCOPES } from '../../constants/scopes.constants';
import { getProductTypeOptions } from './utils/product-state-rules';

const SUCCESS_STEP_INDEX = 4;
const LAST_TAB_STEP_INDEX = tabs.length - 1;

/**
 * Page-level orchestrator for the Configuration flow.
 *
 * Owns only page-level state (loading, error, firstTimeSetup, user,
 * notification, current tab) via the slim {@link ConfigurationDelegate}.
 * Per-tab state lives inside each tab controller; the orchestrator just
 * instantiates them, dispatches `applyFromFi` on load, and composes the
 * submit-all payload from `getValues()` during first-time setup.
 *
 * @extends ClockoutController
 * @extends ConfigurationDelegate
 * @extends SearchParamController
 */
class ConfigurationController extends Inherit(ClockoutController, ConfigurationDelegate, SearchParamController) {
    constructor(props) {
        super(props);
        this.segmentation = new SegmentationController(this);
        this.availableBalance = new AvailableBalanceController(this);
        this.feeStructure = new FeeStructureController(this);
        this.limits = new LimitsController(this);
    }

    async initialize() {
        this.setState({ canModify: this.hasScope(SCOPES.CONFIGURATIONS_MANAGE) });
        await this.loadConfiguration();
    }

    /** Owner and Admin may edit; Member is view-only. Reads cached `state.canModify`. */
    canModifyConfiguration() {
        return Boolean(this.state.canModify);
    }

    // --- Tab navigation (URL-driven) -----------------------------------

    /**
     * Returns the active step index (0..4). Reads from `state.tabKey`
     * first — which is kept in sync with the URL by `navigateToStep` and
     * `handleTabChange` via a real `setState` call — then falls back to a
     * direct URL parse for the initial render before any navigation.
     *
     * @returns {number} active step index (0..4)
     */
    getCurrentStep() {
        const tabKey = this.state?.tabKey ?? this.getSearchParamValue(TAB_SEARCH_PARAM_KEY);
        if (tabKey === SUCCESS_TAB_KEY) return SUCCESS_STEP_INDEX;
        const idx = Number(tabKey);
        if (Number.isFinite(idx) && idx >= 0 && idx <= LAST_TAB_STEP_INDEX) return idx;
        return 0;
    }

    /**
     * Returns the page-level metadata (`title` and `description`) for a
     * given step. The metadata is consumed by the `Page` wrapper.
     */
    getPageMetadata(step = this.getCurrentStep()) {
        return pageMetadata[step] || { title: { text: '', highlight: '' }, description: '' };
    }

    navigateToStep(step) {
        const tabKey = step === SUCCESS_STEP_INDEX ? SUCCESS_TAB_KEY : String(step);
        this.setSearchParamValue(TAB_SEARCH_PARAM_KEY, tabKey);
        this.setState({ tabKey });
    }

    // The `Page` already persisted the new value via the shared
    // SearchParamController; mirror it into React state to trigger the
    // re-render through the normal setState path.
    handleTabChange(key) {
        this.setState({ tabKey: key });
    }

    goToNextStep() {
        const currentStep = this.getCurrentStep();
        if (currentStep < SUCCESS_STEP_INDEX) {
            this.navigateToStep(currentStep + 1);
        } else {
            this.props.navigate('/dashboard');
        }
    }

    goToPreviousStep() {
        const currentStep = this.getCurrentStep();
        if (currentStep > 0) this.navigateToStep(currentStep - 1);
    }

    handleContinue() {
        this.goToNextStep();
    }

    handleCancel() {
        this.props.navigate('/dashboard');
    }

    handleStepChange(step) {
        this.navigateToStep(step);
    }

    // --- Load -----------------------------------------------------------

    async loadConfiguration() {
        try {
            this.setLoading(true);
            const fiData = await this.data.fetchFI();

            // Each applyFromFi is pure: it computes its slice (and seeds
            // its snapshot) without touching React state. Segmentation is
            // computed first because the available-balance sanitizer needs
            // the segmentation slice to filter balance rules. All slices
            // are then committed in a single setState so the page renders
            // exactly once on load.
            const segmentationSlice    = this.segmentation.applyFromFi(fiData);
            const availableBalanceSlice = this.availableBalance.applyFromFi(fiData, segmentationSlice);
            const feeStructureSlice    = this.feeStructure.applyFromFi(fiData);
            const limitsSlice          = this.limits.applyFromFi(fiData);
            const volumeTiersSlice     = this.feeStructure.volumeTiers.applyFromFi(
                feeStructureSlice.fee_rules?.volumeTierRules
            );

            const policy = fiData?.segment_policy;
            const hasSegmentPolicy =
                policy &&
                Object.keys(policy).length > 0 &&
                Object.values(policy).some((value) => {
                    if (Array.isArray(value)) return value.length > 0;
                    return value !== null && value !== undefined && value !== '';
                });

            this.setState({
                firstTimeSetup: !hasSegmentPolicy,
                user: this.data.user,
                // The FI's product catalog, shared by the product-type
                // rules of the Fee Structure, Available Balance and Limits tabs.
                productTypeOptions: getProductTypeOptions(fiData?.product_types),
                tabs: {
                    segmentation:     segmentationSlice,
                    availableBalance: availableBalanceSlice,
                    feeStructure:     feeStructureSlice,
                    limits:           limitsSlice,
                    volumeTiers:      volumeTiersSlice,
                    // Not derived from fiData (see MemberPromosController's
                    // class comment) — this setState replaces `tabs`
                    // wholesale, so without this the slice seeded at mount
                    // (configuration.js's `state` field) would vanish here,
                    // before loadGroups() below gets a chance to repopulate
                    // it, crashing any render in between.
                    memberPromos:     this.feeStructure.memberPromos.getInitialState()
                }
            });

            // Load access list items (whitelist/blocked members) after initial state is set
            await this.segmentation.loadAccessLists();
            // Free-advance promo groups — separate REST-backed data, not part
            // of fi_info, so it loads the same way access lists do: after the
            // initial tabs setState, not derived in applyFromFi.
            await this.feeStructure.memberPromos.loadGroups();
        } catch (error) {
            console.error('Error loading configuration:', error);
            this.setError(true);
        } finally {
            this.setLoading(false);
        }
    }

    // --- Submit-all (first-time setup only) -----------------------------

    /**
     * Composes a single payload from every tab's `getValues()` and saves
     * the whole configuration in one call. Used only during the
     * `firstTimeSetup` flow — outside of it, each tab handles its own
     * per-step submit via `handleSubmit`.
     */
    async handleSubmitAll() {
        if (!this.canModifyConfiguration()) {
            return;
        }
        try {
            this.setLoading(true);
            const aba = this.user?.fi?.aba || this.user?.access?.[0];
            if (!aba) {
                this.showNotification('error', 'Error', 'Unable to find FI ABA');
                return;
            }

            const fi_info = {
                segment_policy: this.segmentation.getValues(),
                ...this.availableBalance.getValues(),
                ...this.feeStructure.getValues(),
                ...this.limits.getValues()
            };

            const response = await this.data.updateFI(aba, { fi_info });

            if (response) {
                this.segmentation.captureSnapshot();
                this.availableBalance.captureSnapshot();
                this.feeStructure.captureSnapshot();
                this.limits.captureSnapshot({ includeAccess: ACCESS_RULES_PERSIST_ENABLED });
                this.setState({ firstTimeSetup: false });
                this.showNotification('success', 'Success', 'All configurations saved successfully');
                this.goToNextStep();
            }
        } catch (error) {
            console.error('Error submitting all configurations:', error);
            this.showNotification(
                'error',
                'Update Failed',
                'Failed to save configurations'
            );
        } finally {
            this.setLoading(false);
        }
    }
}

export default ConfigurationController;
